import { useLoaderData, useNavigate, useSearchParams } from 'react-router-dom';
import React, { useEffect, useState, useRef } from 'react';
import { CalendarInfo } from '../../types/CalendarInfo';
import { Alert } from '@mui/material';
import TeacherHeader from './TeacherHeader';
import TeacherModality from './TeacherModality';
import CoursesSection from './CoursesSection';
import Reservation from './Reservation/Reservation';
import { TeacherInfo } from '../../types/TeacherInfo';
import { Course } from '../../types/Course';
import { useTeacherSearchContext } from '../../providers/TeacherSearchProvider';
import { useReservationContext } from '../../providers/ReservationProvider';
import { trackClickButtonEvent } from '../../utils/functions/trackEvent';
import { verticalCopy } from '../../utils/constants/verticalCopy';
import './TeacherProfile.css';

const groupCoursesByInstitution = (courses: Course[]): Map<string, Course[]> => {
  return courses.reduce((coursesByInstitution, course) => {
    const institutionId = course.institution.value;

    if (!coursesByInstitution.get(institutionId!)) {
      coursesByInstitution.set(institutionId!, []);
    }
    coursesByInstitution.get(institutionId!)?.push(course);

    return coursesByInstitution;
  }, new Map<string, Course[]>());
};

interface TeacherProfileLoaderResponse {
  teacher: TeacherInfo;
  calendarData: Promise<CalendarInfo>;
  course: Promise<Course | null>;
  reservationId: string | null;
}

const SelectedCourse = ({ course }: { course: Course }) => {
  return (
    <React.Fragment>
      <h4 className="selectedCourseTitle">Buscaste:</h4>
      <div className="selectedCourseContainer">
        <p className="selectedCourseLabel">
          {course?.label} - {course?.institution.label}
        </p>
        {course?.notes && (
          <Alert severity="warning">
            <b>Nota: </b>
            {course?.notes}
          </Alert>
        )}
      </div>
    </React.Fragment>
  );
};

const TeacherProfile = () => {
  const {
    teacher: loadedTeacher,
    calendarData,
    course: coursePromise,
    reservationId
  } = useLoaderData() as TeacherProfileLoaderResponse;
  const dictatedCoursesByInstitution: Map<string, Course[]> = groupCoursesByInstitution(
    loadedTeacher.dictated_courses
  );
  const {
    selectedCourse: searchedCourse,
    institutions,
    setSearchType,
    searchType
  } = useTeacherSearchContext();
  const { verifyReservation, reservationStatus } = useReservationContext();
  const typeVariant =
    searchType !== 'all' ? loadedTeacher.teacher_profiles?.[searchType] : undefined;
  const teacher: TeacherInfo = typeVariant
    ? {
        ...loadedTeacher,
        profile_description: typeVariant.profile_description,
        full_description: typeVariant.full_description ?? typeVariant.profile_description,
        price_per_hour: typeVariant.price_per_hour
      }
    : loadedTeacher;
  const [selectedCourse, setSelectedCourse] = useState<Course | undefined>(undefined);
  const [initialStep, setInitialStep] = useState<
    'calendar' | 'checkout' | 'payment' | 'payment_confirmation'
  >(() => {
    // Verificar si hay query param status=rejected al inicializar

    const urlParams = new URLSearchParams(window.location.search);

    if (urlParams.get('status') === 'rejected') {
      return 'payment_confirmation';
    }

    return 'calendar';
  });
  const navigate = useNavigate();
  const hasVerifiedReservation = useRef(false);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const typeParam = searchParams.get('type');

    if (typeParam === 'languages' || typeParam === 'university' || typeParam === 'schools') {
      setSearchType(typeParam);
    } else {
      setSearchType('all');
    }
  }, [searchParams, setSearchType]);

  useEffect(() => {
    setSelectedCourse(teacher.dictated_courses.find((c) => c.value == searchedCourse?.value));
  }, [searchedCourse, teacher]);

  useEffect(() => {
    const handlePromises = async () => {
      const course = await coursePromise;

      if (course) {
        setSelectedCourse(course);
      }
    };

    void handlePromises();
  }, [coursePromise]);

  // Handle reservationId from query parameter - only once
  useEffect(() => {
    if (reservationId && !hasVerifiedReservation.current) {
      console.log('Verifying reservation:', reservationId);
      hasVerifiedReservation.current = true;
      void verifyReservation(reservationId);
    }
  }, [reservationId, verifyReservation]);

  // Set initial step based on reservation status
  useEffect(() => {
    // Si hay un query param "status" con valor "rejected", mantener payment_confirmation
    const statusParam = searchParams.get('status');

    if (statusParam === 'rejected') {
      return;
    }

    if (reservationStatus === 'in_progress') {
      setInitialStep('payment');
    } else if (reservationStatus === 'confirmed') {
      setInitialStep('payment_confirmation');
    } else if (reservationStatus === 'cancelled') {
      // Si hay query param status=rejected, no cambiar a calendar
      if (statusParam === 'rejected') {
        // No hacer nada, mantener el step actual
      } else {
        setInitialStep('calendar');
      }
    }
  }, [reservationStatus, searchParams]);

  const handleBackToResults = () => {
    trackClickButtonEvent('back_to_results');
    if (searchedCourse) {
      const typeParam = searchParams.get('type');
      const typeQuery = typeParam ? `type=${typeParam}&` : '';

      navigate(`/teachers?${typeQuery}courseId=${searchedCourse.value}`);
    } else {
      navigate(`/`);
    }
  };

  return (
    <div className="teacherProfile">
      <TeacherHeader teacher={teacher} onBack={handleBackToResults} />
      <div className="teacherInfo">
        <h2>Sobre el profe</h2>
        <div className="teacherDescription">
          {teacher?.full_description ?? teacher.profile_description}
        </div>
        <h2>Modalidad</h2>
        <TeacherModality teacher={teacher} />
        <h2 style={{ marginBottom: 0 }}>{verticalCopy[searchType].coursesTitle}</h2>
        <CoursesSection
          dictatedCoursesByInstitution={dictatedCoursesByInstitution}
          institutions={institutions}
          searchType={searchType}
        />
        {selectedCourse && <SelectedCourse course={selectedCourse!} />}
      </div>
      <Reservation calendarData={calendarData} teacher={teacher} initialStep={initialStep} />
    </div>
  );
};

export default TeacherProfile;
